import { toHexFromId } from '#~/api/index.js';
import type { IdentityState } from '#~/electron/index.js';
import type { Room } from '@studio/models/room';
import type { AgentRegistryGetRoomResponse } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import {
  createAccountStudioInventory,
  type AccountBootstrapInput,
  type AccountBootstrapRoutingMode,
  type AccountIdentity
} from './account-bootstrap-decision.js';

class UnexpectedIdentityStateError extends Error {
  public constructor(value: never) {
    super(`Unexpected identity state: ${JSON.stringify(value)}`);
    this.name = 'UnexpectedIdentityStateError';
  }
}

/** Account-first cardinality routing is intentionally limited to Iris Electron bootstrap. */
export const resolveAccountBootstrapRoutingMode = (
  isElectron: boolean,
  vendor: string | undefined
): AccountBootstrapRoutingMode => {
  if (!isElectron) return 'browser_legacy';
  return vendor === 'iris' ? 'electron_iris' : 'electron_legacy';
};

export const toBootstrapIdentity = (identity: IdentityState): AccountIdentity => {
  switch (identity.status) {
    case 'resolving':
      return { status: 'resolving' };
    case 'linked':
      return identity.agentId === undefined
        ? { status: 'error', cause: 'readiness_ambiguous' }
        : { status: 'linked', agentId: identity.agentId };
    case 'unlinked':
      return { status: 'unlinked' };
    case 'error':
      return { status: 'error', cause: identity.cause ?? 'readiness_ambiguous' };
    default:
      throw new UnexpectedIdentityStateError(identity.status);
  }
};

export const toAccountBootstrapInput = (
  rooms: readonly AgentRegistryGetRoomResponse[],
  identity: IdentityState,
  options: {
    readonly preferredStudioId: Room['id'] | undefined;
    readonly rememberStudio: boolean;
    readonly routingMode: AccountBootstrapRoutingMode;
  }
): AccountBootstrapInput<AgentRegistryGetRoomResponse> => ({
  inventory: createAccountStudioInventory(rooms, (room) => toHexFromId<Room['id']>(room.id)),
  identity: toBootstrapIdentity(identity),
  linkedStudioId:
    identity.status === 'linked'
      ? toHexFromId<Room['id']>(
          rooms.find((room) =>
            room.agents.some((agent) => toHexFromId(agent.id) === identity.agentId)
          )?.id
        )
      : undefined,
  preferredStudioId: options.preferredStudioId,
  rememberStudio: options.rememberStudio,
  routingMode: options.routingMode
});
